import type {ChatMessage,CodexNotification,AcceptedConversationAttachment} from '@reados/contracts';

/** Local feedback while the API/native thread accepts a submission. These
 * values are never persisted or represented as upstream protocol events. */
export interface PendingSubmission {
  id:string;
  text:string;
  attachments?:AcceptedConversationAttachment[];
  threadId?:string;
  /** Only the POST response can bind this optimistic submission to a native turn. */
  nativeTurnId?:string;
  startedAtMilliseconds:number;
  existingMessageIds:ReadonlySet<string>;
}

export type SubmissionResult=boolean|{accepted:true;turnId?:string;composerTransferred?:boolean};

function presentation(id:string){return {readexWebPresentationID:`pending-user:${id}`,readexWebTurnPresentationID:`pending-turn:${id}`};}

function isCandidate(message:ChatMessage,pending:PendingSubmission):boolean {
  return message.role==='user'&&!pending.existingMessageIds.has(message.id)
    &&message.submissionId===pending.id&&(!pending.threadId||message.threadId===pending.threadId);
}

export function submittedUserMessage(messages:ChatMessage[],pending:PendingSubmission):ChatMessage|undefined {
  if(!pending.nativeTurnId)return undefined;
  return messages.find(message=>message.role==='user'&&message.turnId===pending.nativeTurnId
    &&(!pending.threadId||message.threadId===pending.threadId));
}

export function withPendingSubmission(messages:ChatMessage[],pending:PendingSubmission|null,threadId?:string,submissionIds:Readonly<Record<string,string>>={}):ChatMessage[] {
  const presented=messages.map(message=>{
    const id=message.role==='user'&&typeof message.turnId==='string'?submissionIds[message.turnId]:undefined;
    return id?{...message,...presentation(id)}:message;
  });
  if(!pending)return presented;
  const authoritative=submittedUserMessage(messages,pending);
  if(authoritative)return presented.map(message=>message.id===authoritative.id?{...message,...presentation(pending.id)}:message);
  // SSE can beat the HTTP response. Use the accepted submission identity to avoid a duplicate user bubble,
  // but do not acknowledge or clear local state until POST supplies the turn id.
  const provisional=!pending.nativeTurnId?messages.find(message=>isCandidate(message,pending)):undefined;
  if(provisional)return presented.map(message=>message.id===provisional.id?{...message,...presentation(pending.id)}:message);
  return [...presented,{id:`pending-user:${pending.id}`,role:'user',text:pending.text,status:'complete',attachments:pending.attachments,submissionId:pending.id,
    ...presentation(pending.id),turnId:`pending-turn:${pending.id}`,threadId:pending.threadId??threadId,createdAt:new Date(pending.startedAtMilliseconds).toISOString()}];
}

export function pendingTurnStarted(messages:ChatMessage[],events:CodexNotification[],pending:PendingSubmission):boolean {
  const user=submittedUserMessage(messages,pending);
  if(!user||typeof user.turnId!=='string'||!user.turnId)return false;
  if(messages.some(message=>message.role!=='user'&&message.turnId===user.turnId&&message.threadId===user.threadId))return true;
  return events.some(event=>{
    if(event.method!=='turn/started')return false;
    const turn=event.params.turn;
    if(!turn||typeof turn!=='object')return false;
    const native=turn as Record<string,unknown>;
    // Bind to POST's turn id and its authoritative user item. Replayed history,
    // including identical text, cannot acknowledge a new submission.
    return native.id===user.turnId&&event.params.threadId===user.threadId;
  });
}
